import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import {
  isDemoSession, startDemoSession, endDemoSession, matchesDemoCredentials, DEMO_USER,
} from './demoSession';

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'us-east-2_pjzhWG3xR',
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '1jlo9ijqr1qt717uej3nv9ldh4',
};

const userPool = new CognitoUserPool(poolData);

class AuthService {
  constructor() {
    this.currentUser = null;
    this.idToken = null;
    this.accessToken = null;
    /* Quien necesite recargar datos al cambiar la sesion se apunta aqui. */
    this.sessionListeners = new Set();
  }

  /**
   * Avisa de que la sesion ha cambiado (entrada, entrada en demo, o salida).
   *
   * Existe porque los proveedores de contexto se montan UNA vez, por encima
   * del router y antes del login. Sin esta senal, un proveedor que consulta
   * `isAuthenticated()` en su efecto de montaje lo hace cuando aun no hay
   * nadie dentro, se rinde, y como el login navega con el router -sin recargar
   * la pagina- su efecto no vuelve a ejecutarse nunca.
   *
   * @param {(estaAutenticado: boolean) => void} listener
   * @returns {() => void} funcion para darse de baja
   */
  onSessionChange(listener) {
    this.sessionListeners.add(listener);
    return () => this.sessionListeners.delete(listener);
  }

  /** Notifica a los suscriptores. Un fallo en uno no debe frenar a los demas. */
  notifySessionChange() {
    const estado = this.isAuthenticated();
    this.sessionListeners.forEach((listener) => {
      try {
        listener(estado);
      } catch (err) {
        console.error('Error en un suscriptor de sesión:', err);
      }
    });
  }

  /**
   * Registrar nuevo usuario
   */
  register(email, password, username) {
    return new Promise((resolve, reject) => {
      const attributeList = [
        new CognitoUserAttribute({
          Name: 'email',
          Value: email,
        }),
        new CognitoUserAttribute({
          Name: 'name',
          Value: username,
        }),
      ];

      userPool.signUp(email, password, attributeList, null, (err, result) => {
        if (err) {
          console.error('Error en registro:', err);
          reject(err);
          return;
        }
        resolve(result.user);
      });
    });
  }

  /**
   * Iniciar sesión
   */
  login(email, password) {
    return new Promise((resolve, reject) => {
      // Las credenciales de demostración se atienden aquí y NUNCA llegan a
      // Cognito: esa cuenta no existe en el grupo de usuarios, así que
      // intentarlo sólo produciría un error confuso.
      if (matchesDemoCredentials(email, password)) {
        try {
          resolve({ user: this.loginDemo(), demo: true });
        } catch (err) {
          reject(err);
        }
        return;
      }

      const authenticationData = {
        Username: email,
        Password: password,
      };

      const authenticationDetails = new AuthenticationDetails(authenticationData);

      const userData = {
        Username: email,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          // DEFENSA EN PROFUNDIDAD: al autenticarse de verdad se borra todo
          // rastro de una demostración anterior, aunque el usuario no hubiera
          // salido correctamente de ella. La exclusión mutua de isDemoSession()
          // ya lo cubriría, pero no conviene que quede estado simulado en el
          // navegador de alguien que está viendo datos reales.
          endDemoSession();

          this.idToken = result.getIdToken().getJwtToken();
          this.accessToken = result.getAccessToken().getJwtToken();
          this.currentUser = cognitoUser;

          // Guardar tokens en localStorage
          localStorage.setItem('idToken', this.idToken);
          localStorage.setItem('accessToken', this.accessToken);
          localStorage.setItem('userEmail', email);

          this.notifySessionChange();

          resolve({
            idToken: this.idToken,
            accessToken: this.accessToken,
            user: cognitoUser,
          });
        },
        onFailure: (err) => {
          console.error('Error en login:', err);
          reject(err);
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          // Guardar el usuario temporalmente para el cambio de contraseña
          this.currentUser = cognitoUser;
          localStorage.setItem('tempUserEmail', email);
          
          reject({
            code: 'NewPasswordRequired',
            message: 'Debes cambiar tu contraseña temporal',
            userAttributes,
            requiredAttributes,
          });
        },
      });
    });
  }

  /**
   * Cerrar sesión
   */
  logout() {
    if (this.currentUser) {
      this.currentUser.signOut();
    }

    // Al salir se borra también el estado simulado: si no, los cambios de una
    // demostración reaparecerían en la siguiente.
    endDemoSession();

    // Limpiar localStorage
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userEmail');

    this.currentUser = null;
    this.idToken = null;
    this.accessToken = null;

    this.notifySessionChange();
  }

  /**
   * Verificar si hay sesión activa
   */
  checkSession() {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();

      if (!cognitoUser) {
        // Intentar restaurar desde localStorage
        const savedToken = localStorage.getItem('idToken');
        if (savedToken) {
          this.idToken = savedToken;
          this.accessToken = localStorage.getItem('accessToken');
          resolve(true);
        } else {
          resolve(false);
        }
        return;
      }

      cognitoUser.getSession((err, session) => {
        if (err) {
          console.error('Error checking session:', err);
          reject(err);
          return;
        }

        if (!session.isValid()) {
          resolve(false);
          return;
        }

        // Misma purga que en login: al restaurar una sesión real de Cognito no
        // debe quedar estado de demostración en el navegador.
        endDemoSession();

        this.currentUser = cognitoUser;
        this.idToken = session.getIdToken().getJwtToken();
        this.accessToken = session.getAccessToken().getJwtToken();

        // Actualizar localStorage
        localStorage.setItem('idToken', this.idToken);
        localStorage.setItem('accessToken', this.accessToken);

        this.notifySessionChange();

        resolve(true);
      });
    });
  }

  /**
   * Obtener token ID actual
   */
  getIdToken() {
    return this.idToken || localStorage.getItem('idToken');
  }

  /**
   * Obtener access token actual
   */
  getAccessToken() {
    return this.accessToken || localStorage.getItem('accessToken');
  }

  /**
   * Obtener información del usuario del token
   */
  getUserInfo() {
    // La sesión demo no tiene token que decodificar: su identidad es fija.
    if (isDemoSession()) return { ...DEMO_USER };

    const token = this.getIdToken();
    if (!token) return null;

    try {
      // Decodificar JWT (sin verificar, solo para obtener payload)
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch (err) {
      console.error('Error decoding token:', err);
      return null;
    }
  }

  /**
   * Obtener atributos del usuario
   */
  getUserAttributes() {
    return new Promise((resolve, reject) => {
      if (!this.currentUser) {
        this.currentUser = userPool.getCurrentUser();
      }

      if (!this.currentUser) {
        reject(new Error('No user logged in'));
        return;
      }

      this.currentUser.getUserAttributes((err, attributes) => {
        if (err) {
          reject(err);
          return;
        }

        const attributesObj = {};
        attributes.forEach((attr) => {
          attributesObj[attr.Name] = attr.Value;
        });

        resolve(attributesObj);
      });
    });
  }

  /**
   * Refrescar token
   */
  refreshToken() {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();

      if (!cognitoUser) {
        reject(new Error('No user to refresh'));
        return;
      }

      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }

        const refreshToken = session.getRefreshToken();
        cognitoUser.refreshSession(refreshToken, (err, session) => {
          if (err) {
            reject(err);
            return;
          }

          this.idToken = session.getIdToken().getJwtToken();
          this.accessToken = session.getAccessToken().getJwtToken();

          localStorage.setItem('idToken', this.idToken);
          localStorage.setItem('accessToken', this.accessToken);

          resolve(session);
        });
      });
    });
  }

  /**
   * Verificar si el usuario está autenticado
   */
  isAuthenticated() {
    return !!this.getIdToken() || isDemoSession();
  }

  /**
   * Inicia la sesión de demostración.
   *
   * DELIBERADAMENTE no se emite ningún token: sin credenciales, la sesión demo
   * no puede leer ni escribir datos reales aunque alguna llamada se escapara
   * hacia la API. El aislamiento no depende de que el código se porte bien.
   */
  loginDemo() {
    startDemoSession();
    this.notifySessionChange();
    return { ...DEMO_USER };
  }

  /** ¿La sesión activa es de demostración? */
  isDemo() {
    return isDemoSession();
  }

  /**
   * Cambiar contraseña
   */
  changePassword(oldPassword, newPassword) {
    return new Promise((resolve, reject) => {
      if (!this.currentUser) {
        this.currentUser = userPool.getCurrentUser();
      }

      if (!this.currentUser) {
        reject(new Error('No user logged in'));
        return;
      }

      this.currentUser.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  }

  /**
   * Solicitar código de recuperación de contraseña
   */
  forgotPassword(email) {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.forgotPassword({
        onSuccess: (result) => {
          resolve(result);
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  }

  /**
   * Confirmar nueva contraseña con código
   */
  confirmPassword(email, verificationCode, newPassword) {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          resolve('Password confirmed successfully');
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  }

  /**
   * Completar el cambio de contraseña obligatorio
   */
  completeNewPasswordChallenge(newPassword, userAttributes = {}) {
    return new Promise((resolve, reject) => {
      if (!this.currentUser) {
        reject(new Error('No hay usuario en proceso de cambio de contraseña'));
        return;
      }

      // Asegurar que el atributo name esté presente
      const requiredAttributes = {
        name: userAttributes.name || localStorage.getItem('tempUserEmail')?.split('@')[0] || 'Usuario',
        ...userAttributes
      };

      this.currentUser.completeNewPasswordChallenge(newPassword, requiredAttributes, {
        onSuccess: (result) => {
          this.idToken = result.getIdToken().getJwtToken();
          this.accessToken = result.getAccessToken().getJwtToken();

          // Guardar tokens en localStorage
          localStorage.setItem('idToken', this.idToken);
          localStorage.setItem('accessToken', this.accessToken);
          localStorage.removeItem('tempUserEmail');

          this.notifySessionChange();

          resolve({
            idToken: this.idToken,
            accessToken: this.accessToken,
            user: this.currentUser,
          });
        },
        onFailure: (err) => {
          console.error('Error completando cambio de contraseña:', err);
          reject(err);
        },
      });
    });
  }

  /**
   * Confirmar registro de usuario con código de verificación
   */
  confirmRegistration(email, verificationCode) {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.confirmRegistration(verificationCode, true, (err, result) => {
        if (err) {
          console.error('Error confirmando registro:', err);
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  }

  /**
   * Reenviar código de confirmación
   */
  resendConfirmationCode(email) {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          console.error('Error reenviando código:', err);
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  }

  /**
   * Obtener email temporal guardado durante el proceso de login
   */
  getTempUserEmail() {
    return localStorage.getItem('tempUserEmail');
  }

  /**
   * Limpiar datos temporales
   */
  clearTempData() {
    localStorage.removeItem('tempUserEmail');
    this.currentUser = null;
  }
}

// Instancia única del servicio
const authService = new AuthService();

export default authService;

